import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Field, Input, NativeSelect } from "components/ui";
import { useTranslation } from "translation/useTranslation";

const IMPORT_MODES = {
  existing_employee: "existing_employee",
  new_employees: "new_employees",
};

export default function ScenarioImportDebugMenu({
  tenantIdentifier,
  activePeriod,
}) {
  const { translate, editableTranslate } = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [mode, setMode] = useState(IMPORT_MODES.existing_employee);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [formValues, setFormValues] = useState({
    scenario_slug: "",
    employee_number: "",
    employee_count: 1,
  });

  const selectedScenario = scenarios.find((scenario) => scenario.slug === formValues.scenario_slug) || null;

  useEffect(() => {
    if (!showModal) return undefined;

    document.body.style.overflowY = "hidden";

    return () => {
      document.body.style.overflowY = "auto";
    };
  }, [showModal]);

  useEffect(() => {
    setShowDebugPanel(false);
  }, [formValues.scenario_slug, showModal]);

  const loadScenarios = async () => {
    setLoadingScenarios(true);
    setError(null);

    try {
      const { data, status } = await api.get("/exports/data-seeding-scenarios/balanced-scorecard", {
        params: {
          include_artifact: true,
          per_page: 100,
        },
      });

      if (status === 200) {
        const items = data?.data?.data || [];
        setScenarios(items);
        setFormValues((current) => ({
          ...current,
          scenario_slug: current.scenario_slug || items?.[0]?.slug || "",
        }));
      }
    } catch (err) {
      setError(errorMessage(err, "load balanced scorecard scenarios"));
      Sentry.captureException(err);
    } finally {
      setLoadingScenarios(false);
    }
  };

  const handleOpen = async (e) => {
    e?.preventDefault();

    setShowModal(true);
    setError(null);
    setResult(null);
    setMode(IMPORT_MODES.existing_employee);
    setShowDebugPanel(false);
    setFormValues({
      scenario_slug: "",
      employee_number: "",
      employee_count: 1,
    });
    setScenarios([]);

    await loadScenarios();
  };

  const handleClose = () => {
    setShowModal(false);
    setError(null);
    setResult(null);
    setSubmitting(false);
    setShowDebugPanel(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formValues.scenario_slug) {
      setError({
        message: translate("employee_management.scenario_export.scenario_required", "Szenarioname ist erforderlich."),
      });
      return;
    }

    if (mode === IMPORT_MODES.existing_employee) {
      if (!formValues.employee_number.trim()) {
        setError({
          message: translate("employee_management.scenario_import.employee_number_required", "Personalnummer ist erforderlich."),
        });
        return;
      }
    }

    if (mode === IMPORT_MODES.new_employees) {
      const count = Number(formValues.employee_count);

      if (!Number.isInteger(count) || count < 1 || count > 10) {
        setError({
          message: translate("employee_management.scenario_import.employee_count", "Die Anzahl der Beschäftigten muss zwischen 1 und 10 liegen."),
        });
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      const endpoint = mode === IMPORT_MODES.existing_employee
        ? "/exports/data-seeding-scenarios/balanced-scorecard/import/existing-employee"
        : "/exports/data-seeding-scenarios/balanced-scorecard/import/new-employees";

      const payload = mode === IMPORT_MODES.existing_employee
        ? {
          scenario_slug: formValues.scenario_slug,
          employee_number: formValues.employee_number.trim(),
        }
        : {
          scenario_slug: formValues.scenario_slug,
          employee_count: Number(formValues.employee_count),
        };

      const { data, status } = await api.post(endpoint, payload);

      if (status === 200) {
        setResult(data?.data || null);
        toast.success(data?.message || translate("employee_management.scenario_import.success", "Import erfolgreich"));
      }
    } catch (err) {
      setError(errorMessage(err, "import balanced scorecard scenario"));
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
    }
  };

  const summary = selectedScenario?.summary || {};
  const sourceReference = selectedScenario?.source_reference || {};
  const artifactJson = selectedScenario?.artifact
    ? JSON.stringify(selectedScenario.artifact, null, 2)
    : "";
  const resultScenario = result?.scenario || {};
  const resultSummary = result?.summary || {};
  const resultTarget = result?.target || {};
  const resultTargets = result?.targets || [];
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <a data-testid="import-scenario-menu-item" className="dropdown-item" href="/#" onClick={handleOpen}>
        {editableTranslate("employee_management.import_balanced_scorecard_scenario", "Leistungskartenszenario importieren")}
      </a>

      {showModal && portalTarget && createPortal(
        <>
          <div className={`modal ${showModal ? "fade show d-block" : ""}`} tabIndex="-1" data-testid="modal-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title mb-1">
                        {editableTranslate("employee_management.import_balanced_scorecard_scenario", "Leistungskartenszenario importieren")}
                      </h5>
                      <div className="small text-muted">
                        {(tenantIdentifier || "-")}{activePeriod ? ` / ${activePeriod}` : ""}
                      </div>
                    </div>
                    <Button variant="ghost" size="inline" onClick={handleClose} data-testid="close-button">✕</Button>
                  </div>

                  <div className="modal-body">
                    {error && (
                      <div className="mb-3">
                        <ErrorAlert error={error} onClose={() => setError(null)} />
                      </div>
                    )}

                    <div className="alert alert-info mb-4" role="alert">
                      <div className="font-weight-bold mb-1">
                        {editableTranslate("employee_management.import_balanced_scorecard_scenario.current_period", "Nur der aktuell angemeldete Mandant und die aktuelle Periode")}
                      </div>
                      <div className="small mb-0">
                        {editableTranslate("employee_management.import_balanced_scorecard_scenario.current_period_info", "Dieses Tool importiert das ausgewählte Szenario in den aktuell verwendeten Mandanten und die aktuell verwendete Periode.")}
                      </div>
                    </div>

                    <div className="btn-group btn-group-sm mb-4" role="group">
                      <button
                        type="button"
                        className={`btn ${mode === IMPORT_MODES.existing_employee ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setMode(IMPORT_MODES.existing_employee)} data-testid="scenario-mode-existing-button"
                      >
                        {editableTranslate("employee_management.import_balanced_scorecard_scenario.mode_existing", "Bestehender Beschäftigter")}
                      </button>
                      <button
                        type="button"
                        className={`btn ${mode === IMPORT_MODES.new_employees ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setMode(IMPORT_MODES.new_employees)} data-testid="scenario-mode-new-button"
                      >
                        {editableTranslate("employee_management.import_balanced_scorecard_scenario.mode_new", "Neue Beschäftigte")}
                      </button>
                    </div>

                    <div className="form-group">
                      <Field label={editableTranslate("employee_management.import_balanced_scorecard_scenario.scenario", "Szenario")}>
                      <NativeSelect
                        name="scenario_slug"
                        value={formValues.scenario_slug}
                        onChange={handleChange}
                        disabled={loadingScenarios || submitting} data-testid="scenario-slug-select"
                      >
                        <option value="">
                          {loadingScenarios
                            ? translate("global.loading", "Wird geladen...")
                            : editableTranslate("employee_management.import_balanced_scorecard_scenario.select", "Szenario auswählen")}
                        </option>
                        {scenarios.map((scenario) => (
                          <option key={`${scenario.slug}-${scenario.version}`} value={scenario.slug}>
                            {scenario.name} ({scenario.slug}) v{scenario.version}
                          </option>
                        ))}
                      </NativeSelect>
                      </Field>
                    </div>

                    {selectedScenario && (
                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="pr-3">
                              <div className="font-weight-bold">{selectedScenario.name || selectedScenario.slug}</div>
                              {selectedScenario.description && (
                                <div className="small text-muted mt-1">{selectedScenario.description}</div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="inline"
                              className={`mt-1 ${showDebugPanel ? "text-primary" : "text-muted"}`}
                              icon="bi-bug-fill"
                              onClick={() => setShowDebugPanel((current) => !current)}
                              title={showDebugPanel ? "Hide debug data" : "Show debug data"}
                              aria-label={showDebugPanel ? "Hide debug data" : "Show debug data"}
                              data-testid="toggle-debug-panel"
                            />
                          </div>

                          {showDebugPanel && (
                            <div className="border rounded bg-light px-3 py-3 mb-3">
                              <div className="small text-muted text-uppercase mb-2">Debug data</div>
                              <div className="row small">
                                <div className="col-md-6 mb-3">
                                  <div className="text-muted">Source tenant</div>
                                  <div>{sourceReference.tenant_identifier || "-"}</div>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <div className="text-muted">Source period</div>
                                  <div>{sourceReference.period || "-"}</div>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <div className="text-muted">Source employee number</div>
                                  <div>{sourceReference.employee_number || "-"}</div>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <div className="text-muted">Source employee name</div>
                                  <div>{sourceReference.employee_name || "-"}</div>
                                </div>
                              </div>

                              <div className="small text-muted mb-2">Raw JSON</div>
                              <pre
                                className="small bg-white border rounded p-3 mb-0"
                                style={{ maxHeight: "320px", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                              >
                                {artifactJson || "{}"}
                              </pre>
                            </div>
                          )}

                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <div className="small text-muted">Slug</div>
                              <div>{selectedScenario.slug}</div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="small text-muted">Version</div>
                              <div>{selectedScenario.version}</div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="small text-muted">Schema version</div>
                              <div>{selectedScenario.schema_version}</div>
                            </div>
                          </div>

                          <div className="small text-muted mb-2">Summary</div>
                          <div className="small">
                            {`Objectives: ${summary?.balanced_scorecard?.individual_objectives_count || 0}, `}
                            {`Interviews: ${summary?.balanced_scorecard?.employee_interviews_count || 0}, `}
                            {`Complaints: ${summary?.balanced_scorecard?.complaints_count || 0}`}
                          </div>
                        </div>
                      </div>
                    )}

                    {mode === IMPORT_MODES.existing_employee && (
                      <div className="form-group">
                        <Field label={editableTranslate("field.employee_number", "Personalnummer")}>
                          <Input
                            type="text"
                            name="employee_number"
                            value={formValues.employee_number}
                            onChange={handleChange}
                            disabled={submitting} data-testid="employee-number-input"
                          />
                        </Field>
                      </div>
                    )}

                    {mode === IMPORT_MODES.new_employees && (
                      <div className="form-group">
                        <Field label={editableTranslate("employee_management.import_balanced_scorecard_scenario.employee_count", "Anzahl der Beschäftigten")}>
                          <Input
                            type="number"
                            number
                            min="1"
                            max="10"
                            name="employee_count"
                            value={formValues.employee_count}
                            onChange={handleChange}
                            disabled={submitting} data-testid="employee-count-input"
                          />
                        </Field>
                      </div>
                    )}

                    {result && (
                      <div className="alert alert-success mt-4 mb-0" role="alert">
                        <div className="font-weight-bold mb-2">
                          {editableTranslate("employee_management.import_balanced_scorecard_scenario.success", "Import abgeschlossen")}
                        </div>
                        <div className="small mb-2">
                          {resultScenario.slug ? `${resultScenario.slug}` : ""}{resultScenario.schema_version ? ` / schema ${resultScenario.schema_version}` : ""}
                        </div>

                        {mode === IMPORT_MODES.existing_employee && (
                          <div className="small">
                            <div>{`Employee: ${resultTarget.employee_number || "-"}`}</div>
                            <div>{`Balanced scorecard: ${resultTarget.balanced_scorecard_hash_id || "-"}`}</div>
                            <div>{`Team assignments: ${resultSummary?.decision_summary?.replay_counts?.team_objective_assignments || 0}`}</div>
                          </div>
                        )}

                        {mode === IMPORT_MODES.new_employees && (
                          <div className="small">
                            <div>{`Created employees: ${resultSummary.created_employee_count || 0}`}</div>
                            <div>{`Interview rows: ${resultSummary?.decision_summary?.replay_totals?.employee_interviews_count || 0}`}</div>
                            {resultTargets.length > 0 && (
                              <div className="mt-2">
                                {resultTargets.map((target) => (
                                  <div key={target.employee_hash_id || target.employee_number}>
                                    {`${target.employee_number || "-"} / ${target.email || "-"} / ${target.balanced_scorecard_hash_id || "-"}`}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    <Button variant="secondary" onClick={handleClose} data-testid="close-button">
                      {editableTranslate("button.close", "Schließen")}
                    </Button>
                    <Button data-testid="submit-button"
                      type="submit"
                      variant="primary"
                      loading={submitting}
                      disabled={loadingScenarios || scenarios.length === 0}
                    >
                      {editableTranslate("employee_management.import_balanced_scorecard_scenario.submit", "Szenario importieren")}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className={`modal-backdrop ${showModal ? "show" : ""}`}></div>
        </>,
        portalTarget
      )}
    </>
  );
}
